const formatCurrency = (value) =>
  `$${Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPercent = (value) =>
  `${Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;

const SOURCE_SUMMARIES = {
  fc_motivate: "Profit-share commission based on monthly net profit.",
  email_manager: "Commission payouts for email campaign performance.",
  ace_performance: "ACE Performance bonus tracking against monthly cap.",
  sales_team: "Sales team commission structure and payout schedule.",
};

const PanelSkeleton = () => (
  <div className="p-4 space-y-6">
    <div className="h-4 w-3/4 max-w-md bg-gray-200 animate-pulse rounded" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {[...Array(2)].map((_, i) => (
        <div key={i}>
          <div className="h-4 w-28 bg-gray-200 animate-pulse rounded" />
          <div className="h-8 w-32 bg-gray-200 animate-pulse rounded mt-2" />
        </div>
      ))}
    </div>
  </div>
);

function metricFields({ percentage, attributedRevenue, totalUnpaid, extra = [] }) {
  return {
    fields: [
      {
        label: "Current Percentage",
        value: percentage == null ? "—" : formatPercent(percentage),
      },
      {
        label: "Attributed Revenue",
        value: formatCurrency(attributedRevenue ?? 0),
      },
      {
        label: "Monthly Amount Owed",
        value: formatCurrency(totalUnpaid ?? 0),
      },
      ...extra,
    ],
  };
}

function buildSourceContent(
  source,
  { fcMotivate, fcMotivatePercentage, emailManager, saleTeam, aceProgress }
) {
  switch (source) {
    case "fc_motivate":
      return metricFields({
        percentage: fcMotivate?.percentage ?? fcMotivatePercentage ?? null,
        attributedRevenue:
          fcMotivate?.attributedRevenue ?? fcMotivate?.baseProfit ?? 0,
        totalUnpaid: fcMotivate?.totalUnpaid ?? 0,
      });
    case "email_manager":
      return metricFields({
        percentage: emailManager?.percentage ?? null,
        attributedRevenue: emailManager?.attributedRevenue ?? 0,
        totalUnpaid: emailManager?.totalUnpaid ?? 0,
      });
    case "ace_performance":
      return metricFields({
        percentage: aceProgress?.percentage ?? null,
        attributedRevenue: aceProgress?.attributedRevenue ?? 0,
        totalUnpaid: aceProgress?.totalUnpaid ?? 0,
        extra: [
          {
            label: "Paid / Cap",
            value: `${formatCurrency(aceProgress?.consumed ?? 0)} / ${formatCurrency(aceProgress?.capAmount ?? 0)}`,
          },
          {
            label: "Remaining",
            value: formatCurrency(aceProgress?.remaining ?? 0),
          },
        ],
      });
    case "sales_team":
      return metricFields({
        percentage: saleTeam?.percentage ?? null,
        attributedRevenue: saleTeam?.attributedRevenue ?? 0,
        totalUnpaid: saleTeam?.totalUnpaid ?? 0,
        extra: [
          {
            label: "Monthly Accrued",
            value: formatCurrency(saleTeam?.totalAccrued ?? 0),
          },
        ],
      });
    default:
      return { fields: [] };
  }
}

export default function CommissionsSourcePanel({
  source,
  fcMotivate,
  fcMotivatePercentage,
  emailManager,
  saleTeam,
  aceProgress,
  isLoading = false,
}) {
  const summary = SOURCE_SUMMARIES[source] ?? "";

  if (isLoading) {
    return <PanelSkeleton />;
  }

  const content = buildSourceContent(source, {
    fcMotivate,
    fcMotivatePercentage,
    emailManager,
    saleTeam,
    aceProgress,
  });

  return (
    <div className="p-4">
      <p className="text-sm text-gray-500 mb-6">{summary}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {content.fields.map((field) => (
          <div key={field.label}>
            <p className="text-sm font-medium text-gray-500">{field.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{field.value}</p>
          </div>
        ))}
      </div>

      {content.note && (
        <p className="mt-6 text-sm text-gray-400 italic">{content.note}</p>
      )}
    </div>
  );
}
